import { StyledButtonGroup } from '@app/components/button-group/ButtonGroup';
import { StyledButton } from '@app/components/button/Button';
import { SelectField } from '@app/components/form-fields/SelectField';
import useAgents from '@app/hooks/agents/useAgents';
import useLocations from '@app/hooks/locations/useLocations';
import useNumbers from '@app/hooks/numbers/useNumbers';
import useModal from '@app/hooks/useModal';
import type { AINumber } from '@app/services/api.types';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Flex } from 'antd';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MdCheck } from 'react-icons/md';
import * as z from 'zod';
import { AgentAddModal } from './AgentAddModal';
import { NumberAddModal } from './NumberAddModal';
import { TextField } from '@app/components/form-fields/TextField';

const assignAiSchema = z.object({
    location_id: z.string('Location is required').min(1, 'Select a location'),
    aiAgentId: z.string().optional(),
    number: z.string('Phone Number is required').min(1, 'Select a phone number'),
    failover_number: z.string('Failover number is required').min(1, 'Failover number is required'),
});

export type AssignAiFormValues = z.infer<typeof assignAiSchema>;

export type Option = { id: string; label: string; subLabel?: string };

export type AssignAiAgentFormProps = {
    id: string;
    locations?: Option[];
    locationId?: string;
    agents?: Option[];
    numbers?: Option[];
    initialValues?: Partial<AssignAiFormValues>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSubmit: any; //(values: AssignAiFormValues) => void;
    loading?: boolean;
    onReset?: () => void;
    data?: AINumber;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onCancel?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSave?: any;
};

export const AssignAiAgentForm: React.FC<AssignAiAgentFormProps> = ({
    initialValues,
    onSubmit,
    onCancel,
    ...props
}) => {
    const { data: locations, isLoading } = useLocations({
        per_page: 99999,
    });
    const { data: agents, isLoading: isAgentsLoading } = useAgents({
        per_page: 99999,
        page: 1,
    });
    const { data: numbers, isLoading: isNumbersLoading } = useNumbers({
        per_page: 99999,
        only_unassigned: true,
    });

    const addNumberModal = useModal({});

    const addAiAgentModal = useModal({});

    const {
        control,
        handleSubmit,
        reset,
        watch,
        formState: { isValid, isSubmitting },
    } = useForm<AssignAiFormValues>({
        mode: 'all',
        resolver: zodResolver(assignAiSchema),
        defaultValues: {
            location_id: undefined,
            aiAgentId: undefined,
            number: undefined,
            failover_number: undefined,
            ...initialValues,
        },
    });

    useEffect(() => {
        if (initialValues) {
            reset({
                location_id: initialValues.location_id || undefined,
                aiAgentId: initialValues.aiAgentId || undefined,
                number: initialValues.number || undefined,
                failover_number: initialValues.failover_number || undefined,
            });
        }
    }, [initialValues, reset]);

    const watchLocation = watch('location_id');

    return (
        <form onSubmit={handleSubmit(onSubmit)} noValidate id={props.id}>
            <div className="form-fields-wrapper">
                <Controller
                    control={control}
                    name="location_id"
                    render={({ field, fieldState }) => (
                        <SelectField
                            fieldState={fieldState}
                            {...field}
                            allowClear={false}
                            loading={isLoading}
                            label="Select Location"
                            placeholder="Select a Location"
                            options={
                                locations?.data.flatMap((loc) => {
                                    const toOption = (item: typeof loc) => ({
                                        value: item.id.toString(),
                                        label: `${item.brand_name} - ${item.name}`,
                                    });
                                    const children = loc.children ?? [];
                                    return children.length
                                        ? children.map(toOption)
                                        : [toOption(loc)];
                                }) || []
                            }
                        />
                    )}
                />

                {watchLocation ? (
                    <>
                        <Controller
                            control={control}
                            name="aiAgentId"
                            render={({ field, fieldState }) => (
                                <SelectField
                                    fieldState={fieldState}
                                    {...field}
                                    loading={isAgentsLoading}
                                    allowClear={false}
                                    // label="AI Agent"
                                    label={
                                        <Flex justify="space-between" align="flex-end" gap={20}>
                                            <span>AI Agent</span>
                                            <a
                                                href="#"
                                                className="link"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    addAiAgentModal.open();
                                                }}>
                                                <small>Add New</small>
                                            </a>
                                        </Flex>
                                    }
                                    placeholder="Select an AI Agent"
                                    options={
                                        agents?.data?.map((agent) => ({
                                            value: agent.id.toString(),
                                            label: agent.name,
                                        })) || []
                                    }
                                />
                            )}
                        />

                        <Controller
                            control={control}
                            name="number"
                            render={({ field, fieldState }) => (
                                <SelectField
                                    fieldState={fieldState}
                                    {...field}
                                    loading={isNumbersLoading}
                                    allowClear={false}
                                    label={
                                        <Flex justify="space-between" align="flex-end" gap={20}>
                                            <span>Phone Number</span>
                                            <a
                                                href="#"
                                                className="link"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    addNumberModal.open();
                                                }}>
                                                <small>Add New</small>
                                            </a>
                                        </Flex>
                                    }
                                    placeholder="Select a Phone Number"
                                    options={
                                        numbers?.data?.map((number) => ({
                                            value: number.id,
                                            label: `${number.number}${
                                                number.location?.trim()
                                                    ? ` (${number.location})`
                                                    : ''
                                            }`,
                                        })) || []
                                    }
                                />
                            )}
                        />

                        <Controller
                            control={control}
                            name="failover_number"
                            render={({ field, fieldState }) => (
                                <TextField
                                    fieldState={fieldState}
                                    {...field}
                                    label="Failover Number"
                                    placeholder="Enter a Failover Number"
                                />
                            )}
                        />

                        <Alert
                            type="info"
                            message={
                                <span className="alert-info-message">
                                    <b>Coming Soon:</b> Configure WhatsApp and other messaging
                                    channels for this location.
                                </span>
                            }
                        />
                    </>
                ) : null}

                {/* <StyledButtonGroup justify="flex-end">
                    <StyledButton onClick={handleReset} color="black" data-testid="reset-button">
                        Reset
                    </StyledButton>
                    <StyledButton
                        htmlType="submit"
                        type="primary"
                        color="blue"
                        disabled={!isValid}
                        loading={isSubmitting || isPending}
                        icon={<MdCheck />}
                        data-testid="register-Partner-button">
                        Save Assignment
                    </StyledButton>
                </StyledButtonGroup> */}
            </div>

            <div className="ant-modal-footer">
                <StyledButtonGroup justify="flex-end">
                    <StyledButton onClick={onCancel} color="black" data-testid="reset-button">
                        Cancel
                    </StyledButton>
                    <StyledButton
                        htmlType="submit"
                        type="primary"
                        disabled={!isValid}
                        loading={isSubmitting || props?.loading}
                        icon={<MdCheck />}
                        data-testid="register-Partner-button">
                        Save
                    </StyledButton>
                </StyledButtonGroup>
            </div>

            <NumberAddModal
                open={addNumberModal.isOpen}
                onCancel={() => addNumberModal.reset()}
                onOk={() => addNumberModal.reset()}
            />

            <AgentAddModal
                open={addAiAgentModal.isOpen}
                onCancel={() => addAiAgentModal.reset()}
                onOk={() => addAiAgentModal.reset()}
            />
        </form>
    );
};

export default AssignAiAgentForm;
